import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { leases as leaseApi, review as reviewApi, signups as signupApi } from '@/api/endpoints'
import type { ReviewItem } from '@/api/types'
import { Ago } from '@/components/Ago'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { changeText } from '@/domain/lease'
import { stepWord } from '@/domain/node'
import { leaseFacts, noticeSentence, refusalLine } from '@/domain/review'
import { exact } from '@/ui/format'
import { ClusterDecisionDialog, type DecisionMode } from '../ClusterDecisionDialog'
import { NodeRemoveDialog } from '../NodeRemoveDialog'
import { ChangeApproveDialog } from '../vm/ChangeApproveDialog'
import { CHIP_BRAND, CHIP_DANGER, CHIP_PLAIN } from './chips'
import { GpuApproveDialog } from './GpuApproveDialog'

// 검토 대기의 한 줄: 고르기 · 이름 · 사실 몇 개 · 기다린 시간 · 오른쪽의 조작 칩. 조작은 그 자리에서 끝나고, 끝나면 줄이 사라진다(onDone).
// 칩은 항목의 `actions` 가 허락한 것만 — 서버의 결정이지 화면의 추론이 아니다.
// 체크박스는 일괄로 끝낼 수 있는 줄에만 선다(`domain/review.ts` 의 `batchable`) — 고를지는 부르는 쪽이 정해 `pick` 으로 준다.
// 일괄 조작이 도는 동안(`locked`)은 체크박스가 잠기고, 고른 줄은 칩도 잠긴다 — 같은 항목에 요청이 두 번 가지 않게.

export type RowPick = { checked: boolean; locked: boolean; onChange: (on: boolean) => void }
export type RowProps = { item: ReviewItem; onDone: () => void; pick?: RowPick | undefined }

/** 일괄 조작의 대상이라 이 줄의 칩이 잠겼는가. */
function inBatch(pick: RowPick | undefined): boolean {
  return pick !== undefined && pick.locked && pick.checked
}

export function Row({ item, name, facts, children, error, pick, below }: { item: ReviewItem; name: ReactNode; facts: ReactNode; children: ReactNode; error?: unknown; pick?: RowPick | undefined; below?: ReactNode }) {
  return (
    <li className="flex flex-col gap-1.5 px-4 py-2.5">
      {/* 좁은 화면에서는 이름과 사실이 감기고 칩이 아랫줄 오른쪽에 선다 — 무엇을 승인하는지가 먼저다. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {pick ? (
          <Checkbox aria-label={`${item.subject_name} 선택`} checked={pick.checked} disabled={pick.locked} onCheckedChange={(on) => pick.onChange(on === true)} />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="truncate text-[13px] font-medium max-sm:whitespace-normal">{name}</span>
            <span className="truncate text-[12px] text-muted-foreground max-sm:whitespace-normal">{facts}</span>
          </div>
          <ExpertLine items={[exact(item.since)]} />
        </div>
        <span className="shrink-0 text-[11px] text-muted-foreground"><Ago at={item.since} /></span>
        <div className="flex shrink-0 items-center gap-1.5 max-sm:w-full max-sm:justify-end">{children}</div>
      </div>
      {below}
      <Err error={error} />
    </li>
  )
}

function has(item: ReviewItem, action: ReviewItem['actions'][number]): boolean {
  return item.actions.includes(action)
}

// 가입 신청(F-14): 이름 · 아이디 · 이메일. `승인` 은 비밀번호 메일을 보낸 뒤 계정을 만들고, `반려` 는 확인 하나로 끝난다(신청자에게 가는 것은 없다).
export function SignupRow({ item, onDone, pick }: RowProps) {
  const [busy, setBusy] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const decide = async (act: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await act()
      setRejecting(false)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  const verified = item.detail.email_verified === true
  const facts = [item.detail.username, item.detail.email, verified ? '인증됨' : '미인증'].filter(Boolean).join(' · ')
  return (
    <Row item={item} name={item.subject_name} facts={facts} error={rejecting ? null : error} pick={pick}>
      {has(item, 'approve') ? (
        <button className={CHIP_BRAND} disabled={busy || inBatch(pick)} onClick={() => void decide(() => signupApi.approve(item.subject_id))}>
          {busy && !rejecting ? '승인 중' : '승인'}
        </button>
      ) : null}
      {has(item, 'reject') ? (
        <button className={CHIP_DANGER} disabled={busy || inBatch(pick)} onClick={() => { setError(null); setRejecting(true) }}>
          반려
        </button>
      ) : null}
      <Dialog open={rejecting} onOpenChange={(open) => { if (!open && !busy) setRejecting(false) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{item.subject_name} 가입 반려</DialogTitle></DialogHeader>
          <Err error={error} />
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setRejecting(false)}>취소</Button>
            <Button variant="destructive" disabled={busy} onClick={() => void decide(() => signupApi.reject(item.subject_id))}>{busy ? '반려 중' : '반려'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Row>
  )
}

// GPU 승인: `승인` 은 신청한 사양 아래에서 카드의 모델과 장 수를 고쳐 끝내는 창을 열고,
// `반려` 는 사유를 받는다 — 사유 없는 반려는 신청자가 손쓸 수 없다.
export function ApprovalRow({ item, onDone, pick }: RowProps) {
  const [busy, setBusy] = useState(false)
  const [approving, setApproving] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)
  const reject = async () => {
    if (!reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await leaseApi.reject(item.subject_id, reason.trim())
      setRejecting(false)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <Row item={item} name={item.subject_name} facts={leaseFacts(item)} error={rejecting ? null : error} pick={pick}>
      {has(item, 'approve') ? (
        <button className={CHIP_BRAND} disabled={busy || inBatch(pick)} onClick={() => { setError(null); setApproving(true) }}>
          승인
        </button>
      ) : null}
      {has(item, 'reject') ? (
        <button className={CHIP_DANGER} disabled={busy || inBatch(pick)} onClick={() => { setError(null); setRejecting(true) }}>
          반려
        </button>
      ) : null}
      <GpuApproveDialog item={item} open={approving} onClose={() => setApproving(false)} onDone={() => { setApproving(false); onDone() }} />
      <Dialog open={rejecting} onOpenChange={(open) => { if (!open && !busy) setRejecting(false) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{item.subject_name} 반려</DialogTitle></DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="review-reject-reason">사유</Label>
            <Input id="review-reject-reason" value={reason} autoComplete="off" maxLength={200} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          <Err error={error} />
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setRejecting(false)}>취소</Button>
            <Button variant="destructive" disabled={busy || !reason.trim()} onClick={() => void reject()}>{busy ? '반려 중' : '반려'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Row>
  )
}

// 변경 신청(B-27·B-35): 지금 사양 옆에 적용 뒤의 기계와 사유. 승인은 신청한 숫자가 채워진 창에서
// 고쳐 끝내고(적용은 소유자의 다음 재부팅이다), 반려는 사유를 받는다. 최고 관리자의 화면이라
// 카드를 내주는 변경도 여기서 끝난다.
export function ChangeRow({ item, onDone, pick }: RowProps) {
  const change = item.detail.change
  const [busy, setBusy] = useState(false)
  const [approving, setApproving] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)
  if (!change) return null
  const held = item.detail.gpus ?? 0
  const reject = async () => {
    if (!reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await leaseApi.rejectChange(item.subject_id, change.id, reason.trim())
      setRejecting(false)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  const facts = [leaseFacts(item), `→ ${changeText(change, held)}`, change.reason].join(' — ')
  return (
    <Row item={item} name={item.subject_name} facts={facts} error={rejecting ? null : error} pick={pick}>
      {has(item, 'approve') ? (
        <button className={CHIP_BRAND} disabled={busy || inBatch(pick)} onClick={() => { setError(null); setApproving(true) }}>
          승인
        </button>
      ) : null}
      {has(item, 'reject') ? (
        <button className={CHIP_DANGER} disabled={busy || inBatch(pick)} onClick={() => { setError(null); setRejecting(true) }}>
          반려
        </button>
      ) : null}
      <ChangeApproveDialog
        open={approving}
        title={`${item.subject_name} 변경 승인`}
        leaseId={item.subject_id}
        change={change}
        now={{ cores: item.detail.cores, memory_mb: item.detail.memory_mb, gpus: held, gpu_model: item.detail.gpu_model ?? null }}
        onClose={() => setApproving(false)}
        approve={(body) => leaseApi.approveChange(item.subject_id, change.id, body)}
        onDone={() => { setApproving(false); onDone() }}
      />
      <Dialog open={rejecting} onOpenChange={(open) => { if (!open && !busy) setRejecting(false) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{item.subject_name} 변경 반려</DialogTitle></DialogHeader>
          <div className="grid gap-1.5">
            <Label htmlFor="review-change-reject-reason">사유</Label>
            <Input id="review-change-reject-reason" value={reason} autoComplete="off" maxLength={200} disabled={busy} onChange={(e) => setReason(e.target.value)} />
          </div>
          <Err error={error} />
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setRejecting(false)}>취소</Button>
            <Button variant="destructive" disabled={busy || !reason.trim()} onClick={() => void reject()}>{busy ? '반려 중' : '반려'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Row>
  )
}

// 클러스터 결정: 생성과 연결 두 길. 후보는 항목이 실어 온 것이다.
export function DecisionRow({ item, onDone }: RowProps) {
  const [mode, setMode] = useState<DecisionMode | null>(null)
  const candidates = item.detail.candidates ?? []
  return (
    <Row item={item} name={<Link to={`/nodes/${item.subject_id}`} className="hover:underline">{item.subject_name}</Link>} facts="클러스터를 정해야 합니다.">
      {has(item, 'decide') ? (
        <>
          <button className={CHIP_BRAND} onClick={() => setMode('create')}>클러스터 생성</button>
          <button className={CHIP_PLAIN} onClick={() => setMode('connect')}>클러스터 연결</button>
        </>
      ) : null}
      {mode ? (
        <ClusterDecisionDialog mode={mode} open nodeId={item.subject_id} candidates={candidates} onClose={() => setMode(null)} onDecided={() => { setMode(null); onDone() }} />
      ) : null}
    </Row>
  )
}

// 응답 없는 노드: 잠깐 꺼진 것이면 `무시` 로 알림만 지우고, 빠진 장비면 `제거` 로 플랫폼에서 뺀다. 둘 다 클러스터 화면에서도 할 수 있다.
export function OfflineNodeRow({ item, onDone }: RowProps) {
  const [removing, setRemoving] = useState(false)
  // 걸음에서 멈춘 노드는 어디서 멈췄는지가 첫 사실이다 — 조치는 그 국면이 정한다.
  const stopped = item.detail.step ? `${stepWord(item.detail.step)} 중 멈춤` : null
  const head = [item.detail.cluster?.name, stopped].filter(Boolean).join(' · ')
  const facts = (
    <>
      {head ? `${head} · ` : null}
      {item.detail.last_seen_at ? <>마지막 응답 <Ago at={item.detail.last_seen_at} /></> : '응답한 적 없음'}
    </>
  )
  return (
    <Row item={item} name={<Link to={`/nodes/${item.subject_id}`} className="hover:underline">{item.subject_name}</Link>} facts={facts}>
      {has(item, 'remove') ? <button className={CHIP_DANGER} onClick={() => setRemoving(true)}>제거</button> : null}
      <DismissChip item={item} onDone={onDone} />
      <NodeRemoveDialog open={removing} nodeId={item.subject_id} name={item.subject_name} onClose={() => setRemoving(false)} onRemoved={() => { setRemoving(false); onDone() }} />
    </Row>
  )
}

// 알림: 막힘 한 문장과 `무시`. 배치가 받지 못한 신청(B-46)은 그 아래에 신청한 사양과 노드마다의 이유가 선다 —
// 왜 못 만들었는지를 신청의 기록을 열지 않고 그 자리에서 읽는다.
export function NoticeRow({ item, onDone, pick }: RowProps) {
  const refusals = item.detail.refusals ?? []
  const below = refusals.length > 0 ? (
    <div className={pick ? 'pl-7' : undefined}>
      <p className="text-[12px] text-muted-foreground">{leaseFacts(item)}</p>
      <ul aria-label={`${item.subject_name} 노드별 이유`} className="mt-0.5 text-[12px] text-muted-foreground">
        {refusals.map((r, i) => (
          <li key={`${r.node}:${i}`}>{refusalLine(r)}</li>
        ))}
      </ul>
    </div>
  ) : null
  return (
    <Row item={item} name={item.subject_name} facts={noticeSentence(item.detail)} pick={pick} below={below}>
      <DismissChip item={item} onDone={onDone} pick={pick} />
    </Row>
  )
}

function DismissChip({ item, onDone, pick }: RowProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  if (!has(item, 'dismiss')) return null
  const dismiss = async () => {
    setBusy(true)
    setError(null)
    try {
      await reviewApi.dismiss(item)
      onDone()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }
  return (
    <>
      <button className={CHIP_PLAIN} disabled={busy || inBatch(pick)} onClick={() => void dismiss()}>{busy ? '무시 중' : '무시'}</button>
      {error ? <Err error={error} /> : null}
    </>
  )
}
